import type { Dictionary } from '@/shared/config/locale/ar';

/**
 * English strings (§23.10).
 *
 * Typed as `Dictionary`, which is derived from the Arabic file — so adding a string to
 * `ar.ts` without adding it here is a **compile error**, not something discovered when
 * an operator switches language and sees a key. Arabic is the source of truth because
 * it is the language the product is designed in.
 */
export const en: Dictionary = {
  meta: {
    locale: 'en',
    dir: 'ltr',
    name: 'English',
  },

  app: {
    title: 'Aoun',
    subtitle: 'Control panel',
    signOut: 'Sign out',
    account: 'Account',
    language: 'العربية',
    loading: 'Loading…',
    menuOpen: 'Open the menu',
    menuClose: 'Close the menu',
    menuCollapse: 'Collapse the menu',
    menuExpand: 'Expand the menu',
    liveOn: 'Live updates connected',
    liveConnecting: 'Connecting to live updates…',
    liveOff: 'No live feed — figures refresh from the server',
    appearance: 'Appearance',
    appearanceSystem: 'System',
    appearanceLight: 'Light',
    appearanceDark: 'Dark',
  },

  missions: {
    strandedCreate: 'Stranded report',
    strandedCreateTitle: 'File a stranded report for a caller',
    strandedCreateLede: 'For somebody on the telephone with no app. The search opens at once and the region is called out.',
    strandedNameLabel: 'Name of the stranded person',
    strandedNameRequired: 'The name is required, and at least four characters.',
    strandedPhoneLabel: 'Contact number',
    strandedPhoneRequired: 'The mobile number is required.',
    strandedGenderLabel: 'Gender',
    strandedLatitude: 'Latitude',
    strandedLongitude: 'Longitude',
    strandedWhereRequired: "The position is required — read it off the caller's own phone.",
    strandedCompanions: 'People with them',
    strandedCompanionsInvalid: 'People with them is a whole number, zero or more.',
    strandedNeeds: 'What they need (optional)',
    strandedNeedsPlaceholder: 'e.g. water, a tow, an ambulance',
    strandedConfirm: 'Open the search',
    strandedOpened: 'The search is open and the region has been called out.',
    sending: 'Sending…',
    genders: {
      Male: 'Male',
      Female: 'Female',
    },

    subCreate: 'New sub-mission',
    subCreateTitle: 'Open registration for a sub-mission',
    subCreateLede: 'Hajj, Umrah, an exhibition, an event or a lecture. Names are gathered before the day, and the chat opens when the mission starts rather than at registration.',
    subCreateConfirm: 'Open registration',
    subTitleLabel: 'Name',
    subTitlePlaceholder: 'For example: safety awareness exhibition',
    subTitleRequired: 'A name is required.',
    subKindLabel: 'Kind',
    subRegionLabel: 'Region',
    subCityLabel: 'Governorate',
    subWhenLabel: 'When',
    subWhenRequired: 'The date is required.',
    subWhenPast: 'The date must be in the future.',
    subSeatsLabel: 'Places',
    subSeatsHint: 'Leave blank for no limit',
    subSeatsInvalid: 'Places must be a number greater than zero.',
    subLatitudeLabel: 'Latitude',
    subLongitudeLabel: 'Longitude',
    subWhereRequired: 'The location is required.',
    subDescriptionLabel: 'Description (optional)',
    subCreated: 'Registration is open.',
    subWaiting: 'Waiting list',
    subExclude: 'Remove',
    subExcludeTitle: 'Remove from the mission',
    subExcludeLede: 'Their place is freed for whoever is waiting, and they are told the reason.',
    subExcludeReason: 'Reason',
    subExcludeReasonRequired: 'A reason is required.',
    subExcluded: 'Removed.',
    subPlaces: 'Places',
    subUnlimited: 'No limit',
    subKinds: {
      Hajj: 'Hajj',
      Umrah: 'Umrah',
      Exhibition: 'Exhibition',
      SocialEvent: 'Social event',
      Lecture: 'Lecture',
      Other: 'Other',
    },
    chatLocation: 'Shared location',
    chatHeading: 'Mission chat',
    chatFollowOnly: 'What the team in the field is saying, live.',
    chatReadOnly: 'Follow only',
    chatEmpty: 'Nobody has spoken yet',
    chatEmptyHint: 'The chat opens when the mission starts; the first message appears here as it is sent.',
    chatSentBy: 'Sent by {name}',
    chatVoice: 'Voice note',
    chatImage: 'Photo',
    chatVideo: 'Video clip',
    chatOpenMedia: 'Open',
    chatMediaFailed: 'The file could not be opened. Try again.',
    heading: 'Mission board',
    lede: 'Every mission, with whatever needs a decision at the top.',
    empty: 'No missions match.',
    title: 'Mission',
    type: 'Type',
    status: 'Status',
    region: 'Region',
    area: 'Area',
    members: 'Members',
    elapsed: 'Since incident',
    created: 'Created',
    filterAll: 'All',
    areaUnit: 'km²',
    noRegion: '—',
    notStarted: 'Not started',
    coverageHeading: 'Coverage',
    coverageCovered: 'Covered',
    coverageGap: 'Gap',
    coverageArea: 'Mission area',
    coverageMembers: 'Members',
    coveragePoints: 'Track points',
    coverageRadius: 'Visibility radius',
    positionMinutesAgo: '{n} min ago',
    positionHoursAgo: '{n} h ago',
    coverageUnavailable: 'No coverage is available for this mission.',
    coverageAssumption: 'Coverage is drawn on the visibility radius above, and deliberately carries no percentage — a confident number stops a search early.',
    metres: 'm',

    actions: 'Action',
    publish: 'Approve and start',
    submitToCommittee: 'Send to the committee',
    startMission: 'Start the search',
    close: 'Close',
    startRound: 'New round',
    endRound: 'End round',
    issueCertificates: 'Issue certificates',
    certificatesIssued: '{n} certificate(s) issued.',
    certificatesAlreadyIssued: 'Everyone entitled to one already has it.',
    noActions: '—',
    actionFailed: 'The action could not be carried out.',

    closeDialogTitle: 'Close this mission',
    closeDialogLede: 'The outcome goes into the final report and the statistics, so pick what actually happened.',
    outcomeLabel: 'Outcome',
    outcomePlaceholder: 'Choose the outcome',
    outcomeRequired: 'An outcome is required.',
    summaryLabel: 'Summary (optional)',
    summaryPlaceholder: 'Briefly, what happened',
    confirmClose: 'Confirm closure',
    cancel: 'Cancel',
    closeAria: 'Close',

    roundTitleLabel: 'Round name',
    roundTitlePlaceholder: 'For example: second round — the eastern wadi',
    roundTitleRequired: 'A round name is required.',
    startRoundDialogTitle: 'Start a new round',
    startRoundDialogLede: 'A round re-divides the sectors across the members who are present.',
    confirmStartRound: 'Start the round',
    unitDay: 'd',
    unitHour: 'h',
    unitMinute: 'm',

    // ── Mission detail ────────────────────────────────────────────────────────────


    detailLeader: 'Mission leader',
    detailLeaderColumn: 'Mission control',
    detailNameLeader: 'Name as leader',
    detailStandDownLeader: 'Stand down',
    detailLeaderHint: 'One leader per mission — naming another stands the first down.',
    detailRemoveMember: 'Remove',
    detailRemoveDialogTitle: 'Remove from the mission',
    detailRemoveDialogLede: 'They lose the map and the chat at once, and are told why.',
    detailRemoveReason: 'Reason',
    detailRemoveReasonPlaceholder: 'e.g. left the search area without permission.',
    detailRemoveReasonRequired: 'A reason is required.',
    detailConfirmRemove: 'Remove',
    detailControlOnly: 'For the mission control',

    chatControlCanAnnounce: 'What the team is saying — and you may send an announcement to their handsets.',
    announceHeading: 'Announcement',
    announceLede: 'Appears in the chat as an announcement and reaches every handset on the mission.',
    announcePlaceholder: 'e.g. return to the assembly point, the weather is turning.',
    announceSend: 'Send announcement',

    // ── Mission control (§23.1) ────────────────────────────────────────────────
    controlHeading: 'Control',
    controlHolder: 'On watch',
    controlSince: 'Since',
    controlUnattended: 'Nobody is watching this mission.',
    controlOfferQuestion: 'Do you want to be the control for this mission?',
    controlAccept: 'Yes, I will take it',
    controlDecline: 'No',
    controlYouAccepted: 'You agreed to this mission — waiting for the Head of Control to assign it.',
    controlYouDeclined: 'You declined this mission.',
    controlYouHold: 'You are on watch.',
    controlChangeAnswer: 'Change answer',
    controlCandidates: 'Answers',
    controlNoCandidates: 'Nobody has answered yet.',
    controlAccepted: 'Agreed',
    controlDeclined: 'Declined',
    controlAssign: 'Assign',
    controlAssignHint: 'Assigned to one operator who agreed. One control per mission; it moves by handover.',
    controlHandover: 'Hand over',
    controlHandoverTo: 'Hand over to',
    controlHandoverNobody: 'End with no successor',
    controlHandoverNotes: 'What the next operator needs to know',
    controlHandoverNotesPlaceholder: 'e.g. the northern sector is covered; the second team is in the wadi.',
    controlHandoverConfirm: 'Hand over',
    controlHandoverWarning: 'Ending the watch with no successor leaves the mission unattended.',
    controlHistory: 'Shifts',
    controlShiftEnded: 'Ended',
    controlShiftOpen: 'Open',
    controlHandedTo: 'Handed to',
    controlEndedAlone: 'Ended with no successor',

    detailBack: 'Back to the board',
    detailNotFound: 'No mission with that id.',
    detailOverview: 'Status',
    detailTimes: 'Times',
    detailIncidentAt: 'Incident',
    detailApprovedAt: 'Approved',
    detailStartedAt: 'Started',
    detailClosedAt: 'Closed',
    detailOutcome: 'Outcome',
    detailVisibilityRadius: 'Visibility radius',
    detailIdleThreshold: 'Idle threshold',
    detailNotificationScope: 'Notification scope',
    detailCapacity: 'Capacity',
    detailNone: '—',

    detailCommittee: 'Vote',
    detailApprovals: 'Approve',
    detailRejections: 'Reject',
    detailAbstentions: 'Abstain',
    detailQuorum: 'Quorum',
    detailDeadline: 'Deadline',
    detailAwaitingEscalation: 'Deadline passed — the Head of Control decides',

    detailRounds: 'Rounds',
    detailNoRounds: 'This mission has no rounds.',
    detailRoundSequence: 'Round',
    detailRoundTitle: 'Name',
    detailRoundStarted: 'Started',
    detailRoundEnded: 'Ended',
    detailRoundOpen: 'Open',
    detailRoundArea: 'Area',

    detailMembers: 'Members',
    detailNoMembers: 'Nobody has joined yet.',
    detailMemberName: 'Member',
    detailMemberState: 'State',
    detailMemberSector: 'Sector',
    detailMemberJoined: 'Joined',
    detailMemberDuration: 'Duration',
    detailMemberDistance: 'Distance',
    detailMemberCounted: 'Participation',
    detailCreditParticipation: 'Credit participation',
    detailWithdrawParticipation: 'Withdraw credit',
    detailCreditPending: 'Participation is credited after the mission ends.',
    detailCreditedBy: 'Credited by',
    detailKnight: 'Knight',
    detailNameKnight: 'Name as knight',
    detailWithdrawKnight: 'Withdraw',
    detailKnightUnavailable: 'Only on a missing-person mission where the person was found.',
    detailPoints: 'Points',
    detailCounted: 'Credited',
    detailNotCounted: 'Not credited',
    detailIdleWarned: 'Idle warning issued',
    detailReinstate: 'Reinstate',
    detailReinstateHint: 'Removed for idleness — put them back if they are still searching',
    detailRemoved: 'Removed',
    kilometres: 'km',

    detailReport: 'Closure report',
    detailNoReport: 'No report yet. It is written after closure.',
    detailReportPreparedBy: 'Prepared by',
    detailReportApprovedBy: 'Approved by',
    detailReportPending: 'Awaiting approval',
    detailReportApproved: 'Approved',
    detailWriteReport: 'Write the report',
    detailReviseReport: 'Revise the report',
    detailApproveReport: 'Approve the report',
    detailReportDialogTitle: 'Mission report',
    detailReportDialogLede: 'The outcome belongs to the mission and was fixed at closure — the report describes what happened.',
    detailReportSummaryLabel: 'Report text',
    detailReportSummaryRequired: 'The report text is required.',
    detailReportSave: 'Save',
    detailReportApprovedLocked: 'An approved report cannot be revised.',
    detailRoundReport: 'Round report',
    detailRoundWriteReport: 'Write the round report',
    detailRoundReviseReport: 'Revise the round report',
    detailRoundNoReport: 'Not written yet',
    detailRoundReportDialogTitle: 'Round report',
    detailRoundReportDialogLede: 'What this sweep covered and what it found. Quoted in the approved mission report.',
    detailReportFile: 'Open the report (PDF)',
    detailReportFileOpening: 'Opening…',
    detailReportFileUnavailable: 'The report file could not be opened.',

    detailTimeline: 'Mission timeline',
    detailNoTimeline: 'No events yet.',
    detailTimelineActor: 'by',
    detailTimelineSystem: 'System',
  },

  missionStatus: {
    Draft: 'Draft',
    PendingCommittee: 'With the committee',
    Approved: 'Approved',
    Rejected: 'Refused by the committee',
    Active: 'Active',
    Closed: 'Closed',
    Cancelled: 'Cancelled',
  },

  statistics: {
    heading: 'Association statistics',
    lede: 'What happened in the chosen period, and how the roster stands now.',
    from: 'From',
    to: 'To',
    loading: 'Counting…',

    periodHeading: 'In the period',
    periodLede: 'Moves with the dates. The default window is the last twelve months.',

    rosterHeading: 'Right now',
    rosterLede: 'Unaffected by the dates: this is the roster today, not in a past window.',

    missionsOpened: 'Missions opened',
    participationsCredited: 'Participations credited',
    hoursOnTheGround: 'Hours on the ground',
    distanceCovered: 'Distance covered (km)',
    knightsAwarded: 'Knight awards',
    certificatesIssued: 'Certificates issued',

    missionsRunning: 'Missions running',
    volunteersApproved: 'Approved volunteers',
    volunteersAwaitingReview: 'Awaiting review',

    byMonth: 'Missions month by month',
    byMonthLede: 'An empty month is a zero, not a gap — the quiet season is part of the picture.',
    byOutcome: 'By outcome',
    byType: 'By type',

    noOutcomes: 'No mission closed in this period.',
    noMissions: 'No mission opened in this period.',
    noVolunteers: 'No approved volunteers yet.',
  },

  missionOutcome: {
    FoundAlive: 'Found alive',
    FoundDeceased: 'Found deceased',
    StrandedRescued: 'Stranded person rescued',
    SearchCancelled: 'Search cancelled',
    FoundByAnotherParty: 'Found by another party',
    HandedToAuthorities: 'Handed to the authorities',
    ReturnedByThemselves: 'Returned on their own',
  },


  // ⚠️ The member's state printed as the raw enum name — «Joined», «Apologised» — in a table the
  // control room reads during a search: the same defect already fixed three times over for the
  // mission status, the notification scope and the timeline. Measured on 7 September 2026.
  memberState: {
    Invited: 'Invited',
    Joined: 'Joined',
    Active: 'In the field',
    IdleWarned: 'Idle warning',
    IdleRemoved: 'Removed for idleness',
    Left: 'Left',
    Completed: 'Finished',
    Apologised: 'Withdrew',
    Excluded: 'Removed from the roster',
    Waiting: 'On the waiting list',
  },

  missionType: {
    Missing: 'Missing person',
    Stranded: 'Stranded',
    SubMission: 'Sub-mission',
  },

  notificationScope: {
    AllUsers: 'Everyone',
    SameRegion: 'Volunteers in the same region',
  },

  missionEvent: {
    Created: 'Created',
    SubmittedToCommittee: 'Sent to the committee',
    Approved: 'Approved',
    Rejected: 'Rejected by the committee',
    Started: 'Started',
    MemberJoined: 'Member joined',
    MemberRemoved: 'Member removed',
    MemberReinstated: 'Member reinstated',
    FirstSweepDone: 'First sweep done',
    PersonFound: 'Person found',
    Closed: 'Closed',
    SectorsDivided: 'Sectors divided',
    LeaderNamed: 'Field leader named',
    CommitteeEscalated: 'Committee window expired',
    FoundReported: 'A rescuer reported the find',
  },

  committee: {
    heading: 'Reports awaiting a committee vote',
    lede: 'A vote is a decision about a missing person — and the deadline is running.',
    empty: 'Nothing is awaiting a vote.',
    person: 'Missing person',
    age: 'Age',
    missedAt: 'Last seen',
    lastSeen: 'Last seen at',
    clothing: 'Clothing',
    votes: 'Votes',
    deadline: 'Deadline',
    deadlinePassed: 'Deadline passed',
    approve: 'Approve',
    reject: 'Reject',
    voted: 'You voted',
    restrictedNotes: 'Restricted notes exist',
    voteFailed: 'The vote could not be recorded.',
    quorumReached: 'Quorum reached',
    ofRequired: 'of',
    years: 'years',
  },

  nav: {
    volunteers: 'Volunteers',
    missions: 'Missions',
    committee: 'Committee',
    users: 'Permissions',
    audit: 'Audit trail',
    statistics: 'Statistics',
    exports: 'Exports',
    points: 'Points table',
    branding: 'Branding',
    themes: 'Themes',
    committeeSettings: 'Committee settings',
    sectionOperations: 'Operations',
    sectionAdministration: 'Administration',
  },

  themes: {
    paletteGroup: 'Which palette',
    paletteDay: 'Daylight',
    paletteNight: 'Night',
    noDaylight: 'This theme has no daylight palette, so the colours below are painted in both modes.',
    title: 'Themes',
    subtitle: "The platform's colours, type scale and corners (§7). Edited here rather than in the database, and they reach every device with no new release.",
    heading: 'Themes',
    name: 'Name',
    version: 'Version',
    state: 'State',
    actions: 'Actions',
    live: 'Live',
    draft: 'Draft',
    edit: 'Edit',
    close: 'Close',
    activate: 'Make it live',
    add: 'New theme',
    cancel: 'Cancel',
    save: 'Save',
    saveFailed: 'Could not save.',
    unreadable: "This theme's document cannot be read, so it cannot be edited here.",
    editing: 'Editing',
    editingDraft: 'A draft: saving changes nothing in the field. "Make it live" is what publishes it.',
    editingLive: '⚠️ This theme is live — every save here reaches every device in the field at once.',
    typography: 'Type scale',
    radius: 'Corner radius',
    emptyTitle: 'No themes',
    empty: 'No theme has been created yet.',
    tokens: {
      background: 'Background',
      surface: 'Surface',
      surfaceElevated: 'Raised surface',
      border: 'Border',
      primary: 'Primary',
      primaryForeground: 'On primary',
      secondary: 'Secondary',
      foreground: 'Text',
      mutedForeground: 'Muted text',
      success: 'Success',
      warning: 'Warning',
      danger: 'Danger',
      info: 'Info',
      missionMissing: 'Missing-person mission',
      missionStranded: 'Stranded mission',
      trackLine: 'Track line',
      coverageFill: 'Covered fill',
      gapFill: 'Gap fill',
    },
  },

  points: {
    title: 'Points table',
    subtitle: 'What each kind of participation is worth (§23.6). Edited here, not in the database.',
    heading: 'Items',
    item: 'Item',
    code: 'Code',
    codeHint: 'lowercase letters, digits and hyphens',
    nameAr: 'Arabic name',
    nameEn: 'English name',
    value: 'Points',
    state: 'State',
    active: 'Active',
    retired: 'Retired',
    retire: 'Retire',
    restore: 'Restore',
    add: 'New item',
    cancel: 'Cancel',
    save: 'Save',
    saveFailed: 'Could not save.',
    emptyTitle: 'No items',
    empty: 'Nothing has been added yet.',
  },

  branding: {
    title: 'Branding',
    subtitle: "The association's logo and its certificate seal (§7). Uploaded here.",
    slots: {
      logo: 'Association logo',
      seal: 'Certificate seal',
      vision: 'Vision 2030 mark',
      licence: 'Licence mark',
    },
    ledes: {
      logo: 'Shown in the app, the panel, and at the top of the certificate.',
      seal: 'Stamped at the foot of the certificate of thanks (§23.6).',
      vision: 'Top of the certificate, at the start edge.',
      licence: "The association's ministry licence, at the foot of the certificate.",
    },
    none: 'Nothing uploaded yet',
    loading: 'Loading…',
    keyMissing: 'The theme names a file that is not there. Upload a new one.',
    replace: 'Upload an image',
    uploading: 'Uploading…',
    uploadFailed: 'Could not upload.',
  },

  committeeSettings: {
    title: 'Committee settings',
    subtitle: 'Committee size and voting window (§23.2). Set here, not in a configuration file.',
    heading: 'The committee',
    size: 'Members',
    sizeHint: "The client's answer: 27 members.",
    quorum: 'Quorum',
    quorumNote: 'A majority of the members, derived from the size rather than typed.',
    window: 'Voting window (minutes)',
    windowHint: 'What the referral form is pre-filled with; the Head of Control sets each mission’s own.',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved.',
    saveFailed: 'Could not save.',
  },

  audit: {
    title: 'Audit trail',
    subtitle: 'Who did what, and when. Written with every change in the system (§12.7).',
    filters: 'Filters',
    entries: 'Entries',
    entityType: 'Kind',
    includeSystem: 'Show system rows (refresh tokens and devices)',
    anyEntity: 'Any kind',
    action: 'Action',
    anyAction: 'Any action',
    actions: { Added: 'Added', Modified: 'Modified', Deleted: 'Deleted', Read: 'Read' },
    actor: 'Actor',
    actorPlaceholder: 'User id',
    clear: 'Clear filters',
    when: 'When',
    change: 'Change',
    show: 'Show',
    hide: 'Hide',
    before: 'Before',
    after: 'After',
    nothing: 'Nothing',
    bySystem: 'The system',
    emptyTitle: 'Nothing logged',
    empty: 'Nothing has been recorded yet.',
    emptyNarrowed: 'No entries match these filters.',
  },

  exports: {
    title: 'Exports',
    subtitle: 'Excel workbooks built in the background, then downloaded from here (§19).',
    ask: 'Ask for a workbook',
    askHint: 'Leave the range empty to cover everything. The file is built in the background and appears in the table below.',
    askAction: 'Request export',
    asking: 'Sending…',
    askFailed: 'The request could not be sent. Try again.',
    kind: 'Contents',
    kinds: {
      Missions: 'Missions',
      Volunteers: 'Volunteers',
      Participation: 'Participation',
    },
    from: 'From',
    to: 'To',
    range: 'Range',
    everything: 'Everything',
    openStart: 'From the beginning',
    openEnd: 'Up to now',
    requests: 'Requests',
    status: 'Status',
    statuses: {
      Queued: 'Queued',
      Building: 'Building',
      Ready: 'Ready',
      Failed: 'Failed',
    },
    rows: 'Rows',
    requestedAt: 'Requested',
    file: 'File',
    download: 'Download',
    building: 'Building…',
    failed: 'The export failed.',
    emptyTitle: 'Nothing requested',
    empty: 'No export has been asked for yet.',
  },

  signIn: {
    heading: 'Sign in',
    lede: 'A verification code is sent to your phone.',
    phoneLabel: 'Mobile number',
    phonePlaceholder: '05XXXXXXXX',
    requestCode: 'Send code',
    codeLabel: 'Verification code',
    codePlaceholder: '______',
    verify: 'Verify',
    changeNumber: 'Change number',
    codeSent: 'If the number is registered, a code has been sent.',
    invalidPhone: 'That is not a valid Saudi mobile number.',
    invalidCode: 'The code is 6 digits.',
    rejected: 'That code is wrong or has expired.',
    tooMany: 'Too many attempts. Wait a moment and try again.',
    failed: 'Sign-in failed. Try again.',
    notForYou: 'The dashboard is for association administration. Your work is in the Aoun app.',
  },

  stats: {
    increase: 'increase',
    decrease: 'decrease',
  },

  pagination: {
    label: 'Pagination',
    previous: 'Previous',
    next: 'Next',
    of: 'of',
  },

  users: {
    heading: 'Permissions',
    lede: 'Who can do what. A role is what opens a screen, or closes it.',
    empty: 'Nobody in this list.',
    search: 'Search by name, or by the whole phone number',
    searchHint: 'Phone search matches the whole number, not part of it.',
    searchPlaceholder: 'A name, or a whole phone number',
    confirmRemoveTitle: 'Remove a role',
    confirmRemoveLede: 'A role is what opens a screen, or closes it. Removing it takes effect at once.',
    filters: 'Filters',
    clearFilters: 'Clear',
    roleFilter: 'Role',
    listTitle: 'People',
    emptyFiltered: 'Nobody matches this filter.',
    emptyAll: 'No users yet.',
    name: 'Name',
    phone: 'Phone',
    region: 'Region',
    roles: 'Roles',
    account: 'Account',
    suspend: 'Suspend',
    suspended: 'Suspended — restore',
    noRoles: 'No role',
    add: 'Add role',
    remove: 'Remove',
    filterAll: 'All',
    changeFailed: 'Could not change the role.',
    confirmRemove: 'Remove {role} from {name}?',
    confirmRemoveYes: 'Remove role',
    cancel: 'Cancel',
    roleNames: {
      'reporter': 'Reporter',
      'organisation-representative': 'Organisation representative',
      'field-volunteer': 'Field volunteer',
      'committee-member': 'Committee member',
      'head-of-control': 'Head of Control',
      'desk-control': 'Desk Control',
      'field-leader': 'Field leader',
      'association-employee': 'Association employee',
      'system-administrator': 'System administrator',
    },
  },

  volunteers: {
    heading: 'Volunteers',
    queueHeading: 'Awaiting document review',
    queueLede: 'Approval is what grants a volunteer access to mission data.',
    empty: 'No volunteers in this list.',
    name: 'Name',
    region: 'Region',
    type: 'Type',
    equipment: 'Equipment',
    documents: 'Documents',
    status: 'Status',
    registered: 'Registered',
    actions: 'Action',
    approve: 'Approve',
    reject: 'Reject',
    documentAttached: 'Attached',
    documentMissing: 'None',
    openDocument: 'Open document',
    openCertificate: 'Open certificate',
    openingDocument: 'Opening the document…',
    documentUnavailable: 'The document could not be opened.',
    documentAuditNotice: 'Every time a document is opened it is logged under your name.',
    has4wd: '4WD',
    hasRadio: 'Radio',
    hasGps: 'GPS device',
    noEquipment: '—',
    rejectReasonPrompt: 'Reason for rejection',
    rejectDialogTitle: 'Refuse this volunteer',
    rejectDialogLede: 'The reason is passed on to the volunteer, so write it as you want it read.',
    rejectReasonLabel: 'Reason',
    rejectReasonPlaceholder: 'For example: the identity document is not legible',
    rejectReasonRequired: 'A reason is required.',
    previousRejection: 'Previously refused:',
    accountNeverSignedIn: 'Has not signed in yet — the account activates on first sign-in',
    accountSuspended: 'Account suspended — cannot sign in',
    approveNeedsDocument: 'Cannot approve before an identity document is uploaded.',
    confirmReject: 'Confirm refusal',
    cancel: 'Cancel',
    close: 'Close',
    reviewFailed: 'The review could not be recorded.',
    filterAll: 'All',
  },

  approvalStatus: {
    Pending: 'Awaiting review',
    Approved: 'Approved',
    Rejected: 'Rejected',
  },

  volunteerType: {
    MissingField: 'Field — missing persons',
    StrandedField: 'Field — stranded persons',
    Initiative: 'Initiative',
  },

  attention: {
    title: 'Needs your attention',
    open: 'Notifications',
    close: 'Close',
    nothing: 'Nothing is waiting on you.',
    failed: 'Could not load notifications.',
    foundTitle: 'Found report from the field',
    foundBy: 'Reported by',
    volunteers: 'Volunteers awaiting document review',
    missionWaiting: 'Mission awaiting a decision',
    condition: {
      Safe: 'Safe',
      NeedsMedicalHelp: 'Needs medical help',
      Deceased: 'Deceased',
      unknown: 'Condition not given',
    },
  },

  errors: {
    unauthorized: 'Your session ended. Sign in again.',
    forbidden: 'You do not have permission for that.',
    generic: 'Something went wrong.',
    retry: 'Try again',
  },
};
